import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    getWhatsAppPreference,
    setWhatsAppPreference,
} from "../../application/portalServices";
import WhatsAppIcon from "../../../../shared/ui/icons/WhatsAppIcon";

/**
 * The patient's say over WhatsApp.
 *
 * The number is the therapist's to record; what belongs to the patient is
 * whether it may be used at all — for the automated reminders and for the
 * messages their therapist writes from the agenda alike. On unless they turn
 * it off here, and only they can turn it off or back on. The number is shown
 * so they can tell their therapist if it is wrong, never edited here.
 *
 * Drawn whatever the deployment: even without automated WhatsApp messages, a
 * therapist can still open a chat with the patient from the agenda, and the
 * patient must be able to refuse that too.
 */
export default function WhatsAppRemindersCard() {
    const { t } = useTranslation();

    const [loaded, setLoaded] = useState(false);
    const [allowed, setAllowed] = useState(true);
    const [phone, setPhone] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let cancelled = false;

        getWhatsAppPreference()
            .then((data) => {
                if (cancelled) return;
                setAllowed(data.opt_in);
                setPhone(data.phone);
                setLoaded(true);
            })
            .catch(() => {
                // A preference we cannot read is a section we should not
                // draw: a switch in the wrong position would misreport the
                // patient's own choice back to them.
            });

        return () => {
            cancelled = true;
        };
    }, []);

    if (!loaded) return null;

    const change = async (next: boolean) => {
        const previous = allowed;
        setAllowed(next);
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const data = await setWhatsAppPreference(next);
            setAllowed(data.opt_in);
            setPhone(data.phone);
            setSaved(true);
        } catch {
            setAllowed(previous);
            setError(t("portal.whatsapp.errors.saveFailed"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="glass-effect glass-border mb-6 rounded-2xl border p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
                <WhatsAppIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h2 className="text-base font-semibold text-content-primary">
                    {t("portal.whatsapp.title")}
                </h2>
            </div>

            <p className="mb-4 text-sm text-content-tertiary">
                {t("portal.whatsapp.description")}
            </p>

            <label className="mb-4 flex min-h-11 items-start gap-3 text-sm text-content-secondary">
                <input
                    type="checkbox"
                    role="switch"
                    checked={allowed}
                    disabled={saving}
                    onChange={(e) => void change(e.target.checked)}
                    className="mt-0.5 h-4 w-4"
                />
                <span>{t("portal.whatsapp.optInLabel")}</span>
            </label>

            <div className="mb-3 text-sm">
                <p className="font-medium text-content-secondary">
                    {t("portal.whatsapp.phoneLabel")}
                </p>
                {phone ? (
                    <>
                        <p className="text-content-primary">{phone}</p>
                        <p className="mt-1 text-xs text-content-tertiary">
                            {t("portal.whatsapp.phoneHint")}
                        </p>
                    </>
                ) : (
                    <p className="text-content-tertiary">{t("portal.whatsapp.noPhone")}</p>
                )}
            </div>

            {error && (
                <p role="alert" className="text-sm text-red-600">
                    {error}
                </p>
            )}

            {saved && !error && (
                <p role="status" className="text-sm text-green-700 dark:text-green-400">
                    {allowed ? t("portal.whatsapp.savedOn") : t("portal.whatsapp.savedOff")}
                </p>
            )}
        </section>
    );
}
